// Generic error messages for different contexts - hides backend details from users
export const ERROR_MESSAGES = {
  default: "Failed, try again later.",
  network: "Unable to connect. Please check your internet connection.",
  auth: "Authentication failed. Please check your credentials and try again.",
  mfa: "Verification failed. Please check your code and try again.",
  session: "Session expired. Please login again.",
  submit: "Failed to submit. Please try again later.",
  delete: "Failed to delete. Please try again.",
  update: "Failed to update. Please try again.",
  create: "Failed to create. Please try again.",
  load: "Failed to load data. Please refresh the page.",
  email: "Failed to send email. Please try again.",
  save: "Failed to save. Please try again.",
  fetch: "Failed to fetch data. Please try again.",
  registration: "Failed to submit registration. Please try again.",
  password: "Failed to change password. Please try again.",
  profile: "Failed to update profile. Please try again.",
  status: "Failed to update status. Please try again.",
  reminder: "Failed to send reminder. Please try again.",
  approval: "Failed to process approval. Please try again.",
  rateLimit: "Too many attempts. Please wait a moment and try again.",
  passwordReset: "Failed to reset password. Please try again.",
  refresh: "Failed to refresh. Please try again.",
  sync: "Failed to sync data. Please try again.",
  disconnect: "Failed to disconnect. Please try again.",
  upload: "Failed to upload. Please try again.",
  generate: "Failed to generate. Please try again.",
  trigger: "Failed to trigger. Please try again.",
  notifications: "Could not enable notifications. Please try again later.",
  push_test: "Test failed. Please try again later.",
  voice_agent: "Action failed. Please try again later.",
  bookings: "Booking action failed. Please try again later.",
  media_invoice: "Invoice action failed. Please try again later.",
  public_form_load: "Unable to load this form. Please try again later.",
  public_form_submit: "Failed to submit your response. Please review and try again.",
  public_form_upload: "Failed to upload your file. Please try again.",
  forms_submissions: "Failed to load submissions. Please try again.",
  forms_share: "Failed to update share settings. Please try again.",
  forms_analytics: "Failed to load analytics. Please try again.",
  forms_workflows: "Failed to load workflows. Please try again.",
  forms_workflow_run: "Failed to run workflow. Please try again.",
  forms_templates: "Failed to load templates. Please try again.",
  forms_ai: "AI request failed. Please try again.",
  forms_import: "Failed to import file. Please check the format and try again.",
  forms_settings: "Failed to update settings. Please try again.",
  forms_permissions: "Failed to update permissions. Please try again.",
  forms_approvals: "Failed to process approval. Please try again.",
  forms_kiosk: "Kiosk mode error. Please try again.",
  subscription_plans: "Failed to load subscription plans. Please try again.",
  saas_signup: "Failed to create your workspace. Please try again.",
};

export type ErrorContext = keyof typeof ERROR_MESSAGES;

/**
 * Checks if an error is a rate limiting error (HTTP 429)
 */
export const isRateLimitError = (error: unknown): boolean => {
  if (!error) return false;
  const err = error as any;
  if (err.status === 429) return true;
  if (err.name === 'AuthApiError' && err.status === 429) return true;
  const message = err.message?.toLowerCase() || '';
  if (message.includes('rate limit') || message.includes('too many requests')) {
    return true;
  }
  return false;
};

/**
 * Detects strings that look like raw backend / infra error leaks.
 * Used by the toast sanitization layer as defense in depth — any text
 * matching this is replaced with a generic message before reaching the UI.
 */
const LEAK_PATTERNS: RegExp[] = [
  /\brate ?limit(ed)?\b/i,
  /\bvapid\b/i,
  /\bsupabase\b/i,
  /\bservice[_ ]?role\b/i,
  /\bjwt\b/i,
  /\bRLS\b/,
  /\bpg_[a-z_]+/i,
  /\bP\d{4}\b/,                // Postgres SQLSTATE
  /\bcode:\s*['"]?\w+/i,
  /\bduplicate key\b/i,
  /\binvalid input\b/i,
  /\brelation "[^"]+"/i,
  /\bcolumn "[^"]+"/i,
  /\b(429|500|502|503|504)\b/,
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i, // UUID
  /https?:\/\/[^\s]+/i,
  /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/, // email
  /\bat\s+[A-Za-z_$][\w$.]*\s*\(/,                  // stack frame
  /[{}\[\]]/,                                        // raw JSON braces
  /\bFunctionsHttpError\b/i,
  /\bFunctionsRelayError\b/i,
  /\bEdge Function\b/i,
];

export const looksLikeBackendLeak = (text: unknown): boolean => {
  if (typeof text !== 'string') return false;
  if (!text.trim()) return false;
  if (text.length > 160) return true;
  return LEAK_PATTERNS.some((rx) => rx.test(text));
};

/**
 * Logs error to console and returns a generic user-friendly message.
 * Backend error details are NEVER exposed to users.
 */
export const handleError = (
  error: unknown,
  context?: ErrorContext
): string => {
  console.error('Error:', error);
  if (isRateLimitError(error)) return ERROR_MESSAGES.rateLimit;
  return ERROR_MESSAGES[context || 'default'];
};

/**
 * Synchronous, side-effect-free version of handleError. Always returns a
 * generic message — never the raw `error.message`.
 */
export const safeMessage = (
  error: unknown,
  context?: ErrorContext
): string => {
  if (isRateLimitError(error)) return ERROR_MESSAGES.rateLimit;
  return ERROR_MESSAGES[context || 'default'];
};

/**
 * Get a generic error message for a specific context
 */
export const getErrorMessage = (context: ErrorContext): string => {
  return ERROR_MESSAGES[context];
};

/**
 * Sanitize a user-facing string. If it looks like a leaked backend error,
 * replace it with the generic message for the given context. Otherwise
 * pass it through unchanged.
 */
export const sanitizeUserText = (
  text: unknown,
  context?: ErrorContext
): string | undefined => {
  if (text === undefined || text === null) return undefined;
  const s = typeof text === 'string' ? text : String(text);
  if (looksLikeBackendLeak(s)) {
    return ERROR_MESSAGES[context || 'default'];
  }
  return s;
};
